import { Button } from "@xometry/xometry_loft";
import React, { Suspense, lazy, useContext, useMemo } from "react";
import Helmet from "react-helmet";
import { UserValidationContext } from "../../utils/user-validation-context";
import {
  RFQContainerStandAlone,
  RFQContainerBase,
  SharedQuoteUploadWrapper,
  CTAWrapper,
  CTAWrapperShared,
  SliceWrapper,
  CompactDualCTAWrapper,
  NestedCTADisplayWrapper,
  QuoteUploadWithRfqWrapper,
  LoaderWrapper,
  Loader,
} from "./customer-quote-request.style";
import UploadFilePanel from "../upload-file-panel/upload-file-panel";
import { getDomain, origins } from "../../utils/environment-urls";
import { CustomerRfqCtaSlice } from "../../types/graphql";
import FormStepperComponentType, { FormProps } from "./form-stepper-component";
import { ErrorBoundary } from "react-error-boundary";
import { addImportDependencies } from "../../utils/lazyLoad";
import { createRoot, Root } from "react-dom/client";
import * as FA from "../../styles/fontawesome";

let gQuoteRequestRoot: Root | null = null;

export function RFQOnlyCTA({
  standAlone,
  prompt = "Send us your files, and we'll build the quote for you!",
  buttonText = "Start an RFQ",
}: {
  standAlone?: boolean;
  prompt?: string;
  buttonText?: string;
}) {
  const WrapperComponent = standAlone
    ? RFQContainerStandAlone
    : RFQContainerBase;

  const divId = "rfqContainer";

  return (
    <>
      <WrapperComponent
        tabIndex={0}
        role="button"
        onClick={() => openFormMicroComponent(divId, customerQuoteRequestProps)}
      >
        <i css={FA.CloudArrowUpRegular} />
        <Button kind="primary">{buttonText}</Button>
        <span className="prompt">{prompt}</span>
      </WrapperComponent>
      <div id={divId} />
    </>
  );
}

export function QuoteUploadComponentInner() {
  const userValidation = useContext(UserValidationContext);
  const userData = useMemo(
    () =>
      userValidation.isReady &&
      userValidation.isValid &&
      !userValidation.isExpired &&
      userValidation.userData
        ? userValidation.userData
        : null,
    [
      userValidation.isReady,
      userValidation.isExpired,
      userValidation.isValid,
      userValidation.userData,
    ]
  );

  const LoadingSkeletonCard = () => <div />;

  return (
    <UploadFilePanel
      Fallback={LoadingSkeletonCard}
      panelStyle="transparent"
      showSupportedFileTypes={true}
      layoutDirection="vertical"
      showInstantQuoteButton={true}
      panelScale="large"
      instantQuoteButtonContent={
        <>
          <i css={[FA.CloudArrowUpRegular]} />
          &nbsp;Start a New Instant Quote
        </>
      }
    >
      <SharedQuoteUploadWrapper>
        <div className="spotDownload">
          <img
            src="https://www.xometry.com/quoting/home/assets/images/download-to-folder.png"
            alt="Upload files"
          />
        </div>
        <div className="uploadDragDrop">Drag &amp; Drop or Select Files</div>
      </SharedQuoteUploadWrapper>
    </UploadFilePanel>
  );
}

export function QuoteUploadWithRFQ() {
  return (
    <CTAWrapperShared>
      <QuoteUploadWithRfqWrapper>
        <QuoteUploadComponentInner />
      </QuoteUploadWithRfqWrapper>
      <CustomerQuoteRequest />
    </CTAWrapperShared>
  );
}

export function QuoteUploadOnly() {
  return (
    <CTAWrapper>
      <QuoteUploadComponentInner />
    </CTAWrapper>
  );
}

export function QuoteUploadWithRFQCompact({
  rfqTitle,
  buttonText,
  prompt,
}: {
  rfqTitle?: string;
  buttonText?: string;
  prompt?: string;
}) {
  return (
    <CompactDualCTAWrapper>
      <h6>Build A Quote</h6>
      <CTAWrapper>
        <QuoteUploadComponentInner />
      </CTAWrapper>
      <NestedCTADisplay
        prompt={prompt}
        buttonText={buttonText}
        rfqTitle={rfqTitle}
      />
    </CompactDualCTAWrapper>
  );
}

export default function CustomerQuoteRequest({
  compact,
  standAlone,
  buttonText,
  rfqTitle,
  prompt,
}: {
  compact?: boolean;
  standAlone?: boolean;
  buttonText?: string;
  rfqTitle?: string;
  prompt?: string;
}) {
  if (!standAlone && compact) {
    return (
      <QuoteUploadWithRFQCompact
        buttonText={buttonText}
        prompt={prompt}
        rfqTitle={rfqTitle}
      />
    );
  }

  return (
    <RFQOnlyCTA
      standAlone={standAlone}
      prompt={prompt}
      buttonText={buttonText}
    />
  );
}

const NestedCTADisplay = ({
  prompt = "For purchasing and supply chain professionals submitting complete quote packages.",
  buttonText = "Start an RFQ",
  rfqTitle = "Request A Quote",
}: {
  prompt?: string;
  buttonText?: string;
  rfqTitle?: string;
}) => {
  const divId = "rfqContainer";
  return (
    <>
      <NestedCTADisplayWrapper>
        <h6>{rfqTitle}</h6>
        <div className="nestedCtaContent">
          <p>{prompt}</p>
          <Button
            onClick={() =>
              openFormMicroComponent(divId, customerQuoteRequestProps)
            }
          >
            {buttonText}
          </Button>
        </div>
      </NestedCTADisplayWrapper>
      <div id={divId} />
    </>
  );
};

function ErrorFallback({ error }: any) {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
    </div>
  );
}

export const openFormMicroComponent = (
  divName: string,
  formProps: FormProps
) => {
  if (gQuoteRequestRoot) {
    gQuoteRequestRoot.unmount();
    gQuoteRequestRoot = null;
  }
  const rfqContainer = document.getElementById(divName);
  if (rfqContainer) {
    gQuoteRequestRoot = createRoot(rfqContainer);
    gQuoteRequestRoot.render(<FormMicroComponent {...formProps} />);
  }
};

export const closeFormMicroComponent = (divName: string) => {
  if (gQuoteRequestRoot) {
    gQuoteRequestRoot.unmount();
    gQuoteRequestRoot = null;
  }
};

export const customerQuoteRequestProps: FormProps = {
  config: {
    publicUrl: "/",
    routerLocation: "/customer-quote-request-modal",
    service: "marketing-frontend",
    baseDomain: getDomain() || "xometry.test",
    origins: {
      partFilesService: origins.pfs,
      www: origins.www,
      auth: origins.auth,
      get: origins.get,
      graphql: origins.graphql,
    },
  },
};

export const singleFormProps: FormProps = {
  config: {
    publicUrl: "/",
    routerLocation: "/single-form",
    service: "marketing-frontend",
    baseDomain: getDomain() || "xometry.test",
    origins: {
      partFilesService: origins.pfs,
      www: origins.www,
      auth: origins.auth,
      get: origins.get,
      graphql: origins.graphql,
    },
  },
};

const CustomerQuoteRequestComponent = lazy(() => {
  const isSSR = typeof window === "undefined";

  if (!isSSR) {
    addImportDependencies();
  }
  window.marketplaceFrontendUrl = `${origins.marketplace}/mf-static/`;

  type Module = { default: typeof FormStepperComponentType };
  return window.dynamicImport(
    `${origins.marketplace}/mf-static/component.mjs`
  ) as Promise<Module>;
});

export const FormMicroComponent: React.FC<FormProps> = (
  formProps: FormProps
): JSX.Element => {
  // populate publicUrl dynamically
  formProps.config.publicUrl = window.location.pathname;
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Helmet>
        <link
          key="fontawesome"
          rel="stylesheet"
          type="text/css"
          href="https://assets.xometry.com/fontawesome-pro/v6/css/all.min.css"
        />
      </Helmet>
      {/* TODO: Add loader */}
      <Suspense
        fallback={
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        }
      >
        <CustomerQuoteRequestComponent {...formProps} />
      </Suspense>
    </ErrorBoundary>
  );
};

export function CustomerRfqCtaSliceComponent({
  slice,
}: {
  slice: CustomerRfqCtaSlice;
}) {
  const { customer_rfq_cta_button_text, customer_rfq_cta_subtitle } =
    slice.primary;
  return (
    <SliceWrapper>
      <CustomerQuoteRequest
        buttonText={customer_rfq_cta_button_text || undefined}
        prompt={customer_rfq_cta_subtitle?.text || undefined}
        standAlone={true}
      />
    </SliceWrapper>
  );
}
